import type { AxiosInstance } from "axios";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import type { OpenInvitationEventOwnerView } from "@shared/schemas/open-invitation.schemas";
import type {
  CreateOpenInvitationBody,
  CreateMagicLinksBody,
  EditDefaultInvitationPhraseBody,
} from "@shared/schemas/open-invitation-write.schemas";
import type { OpenInvitationWithMagicLinks } from "@shared/schemas/open-invitation-with-magic-links.schemas";

const basePath = (eventIdPublic: EventIdPublic): string =>
  `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/invitations/open`;

export function createOpenInvitationsApi(apiClient: AxiosInstance) {
  return {
    async getByEvent(
      eventIdPublic: EventIdPublic,
    ): Promise<OpenInvitationEventOwnerView[]> {
      const response = await apiClient.get<OpenInvitationEventOwnerView[]>(
        basePath(eventIdPublic),
      );
      return response.data;
    },

    async getByEventResolved(
      eventIdPublic: EventIdPublic,
    ): Promise<OpenInvitationWithMagicLinks[]> {
      const response = await apiClient.get<OpenInvitationWithMagicLinks[]>(
        `${basePath(eventIdPublic)}/resolved`,
      );
      return response.data;
    },

    async create(
      data: CreateOpenInvitationBody & {
        eventIdPublic: EventIdPublic;
      },
    ): Promise<OpenInvitationEventOwnerView> {
      const { eventIdPublic, ...body } = data;
      const response = await apiClient.post<OpenInvitationEventOwnerView>(
        basePath(eventIdPublic),
        body,
      );
      return response.data;
    },

    async disable(
      eventIdPublic: EventIdPublic,
      idPublic: string,
    ): Promise<OpenInvitationEventOwnerView> {
      const response = await apiClient.post<OpenInvitationEventOwnerView>(
        `${basePath(eventIdPublic)}/${idPublic}/disable`,
      );
      return response.data;
    },

    async editDefaultPhrase(
      eventIdPublic: EventIdPublic,
      idPublic: string,
      data: EditDefaultInvitationPhraseBody,
    ): Promise<OpenInvitationEventOwnerView> {
      const response = await apiClient.post<OpenInvitationEventOwnerView>(
        `${basePath(eventIdPublic)}/${idPublic}/default-phrase`,
        data,
      );
      return response.data;
    },

    async createMagicLinks(
      eventIdPublic: EventIdPublic,
      idPublic: string,
      data: CreateMagicLinksBody,
    ): Promise<OpenInvitationWithMagicLinks> {
      const response = await apiClient.post<OpenInvitationWithMagicLinks>(
        `${basePath(eventIdPublic)}/${idPublic}/magic-links`,
        data,
      );
      return response.data;
    },
  };
}
